import { Download, FileSpreadsheet, FileText } from 'lucide-react';

import type { ChatDto, FileAssetDto, MessageDto } from '../carrierCabinet/api';

export function mergeMessages(current: MessageDto[], incoming: MessageDto[]) {
  const messages = new Map(current.map((message) => [message.id, message]));
  incoming.forEach((message) => messages.set(message.id, { ...messages.get(message.id), ...message }));
  return [...messages.values()].sort((left, right) => left.id - right.id);
}

export function updateChatWithMessage(chats: ChatDto[], chatId: number, message: MessageDto, currentUserId: number) {
  return chats
    .map((chat) => chat.id === chatId
      ? {
          ...chat,
          last_message: message,
          unread_count: message.sender !== currentUserId ? chat.unread_count + 1 : chat.unread_count,
        }
      : chat)
    .sort((left, right) => {
      const leftDate = left.last_message?.created_at ?? left.created_at;
      const rightDate = right.last_message?.created_at ?? right.created_at;
      return new Date(rightDate).getTime() - new Date(leftDate).getTime();
    });
}

export function formatFileSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateChatAttachment(file: File) {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const image = ['jpg', 'jpeg', 'png', 'webp'].includes(extension);
  const video = ['mp4', 'mov', 'webm'].includes(extension);
  const document = ['pdf', 'docx', 'xlsx'].includes(extension);
  if (!image && !video && !document) return 'Unsupported file type.';
  const limit = image ? 10 * 1024 * 1024 : video ? 200 * 1024 * 1024 : 15 * 1024 * 1024;
  if (file.size > limit) return `File is larger than ${image ? 10 : video ? 200 : 15} MB.`;
  return null;
}

export function ChatAttachment({ file, alt }: { file: FileAssetDto; alt: string }) {
  if (file.media_kind === 'video') {
    return <video className="chat-attachment-video" controls preload="metadata" src={file.url} />;
  }
  if (file.media_kind === 'document') {
    const spreadsheet = file.content_type?.includes('spreadsheet');
    const Icon = spreadsheet ? FileSpreadsheet : FileText;
    return (
      <a className="chat-attachment-document" href={file.download_url ?? file.url} download={file.original_filename}>
        <Icon aria-hidden="true" size={24} />
        <span><strong>{file.original_filename}</strong><small>{formatFileSize(file.size_bytes)}</small></span>
        <Download aria-hidden="true" size={20} />
      </a>
    );
  }
  return <img src={file.url} alt={alt} />;
}

export const SYSTEM_MESSAGES_EN_TO_RU: Record<string, string> = {
  'The customer opened a private chat to discuss the transport terms.': 'Заказчик открыл приватный чат для обсуждения условий перевозки.',
  'The customer selected another carrier. This chat is read-only.': 'Заказчик выбрал другого перевозчика. Этот чат доступен только для чтения.',
  'Offer accepted. LOVO does not accept payment for transport: the parties agree on direct payment terms in the chat.': 'Отклик принят. LOVO не принимает оплату за перевозку: условия прямого расчета стороны согласуют в чате.',
  'Direct payment between customer and carrier was agreed.': 'Согласована прямая оплата между заказчиком и перевозчиком.',
  'Customer marked direct payment as paid.': 'Заказчик отметил прямую оплату как оплаченную.',
  'Carrier confirmed direct payment receipt.': 'Перевозчик подтвердил получение прямой оплаты.',
  'Carrier started the transport.': 'Перевозчик начал перевозку.',
  'Carrier marked the transport as delivered.': 'Перевозчик отметил доставку питомца.',
  'Customer confirmed receiving the pet.': 'Заказчик подтвердил получение питомца.',
};

export const SYSTEM_MESSAGES_RU_TO_EN: Record<string, string> = {
  'Заказчик открыл приватный чат для обсуждения условий перевозки.': 'The customer opened a private chat to discuss the transport terms.',
  'Заказчик выбрал другого перевозчика. Этот чат доступен только для чтения.': 'The customer selected another carrier. This chat is read-only.',
  'Отклик принят. LOVO не принимает оплату за перевозку: условия прямого расчета стороны согласуют в чате.': 'Offer accepted. LOVO does not accept payment for transport: the parties agree on direct payment terms in the chat.',
  'Согласована прямая оплата между заказчиком и перевозчиком.': 'Direct payment between customer and carrier was agreed.',
  'Заказчик отметил прямую оплату как оплаченную.': 'Customer marked direct payment as paid.',
  'Перевозчик подтвердил получение прямой оплаты.': 'Carrier confirmed direct payment receipt.',
  'Перевозчик начал перевозку.': 'Carrier started the transport.',
  'Перевозчик отметил перевозку как доставленную.': 'Carrier marked the transport as delivered.',
  'Перевозчик отметил доставку питомца.': 'Carrier marked the transport as delivered.',
  'Заказчик подтвердил получение питомца.': 'Customer confirmed receiving the pet.',
};

export function formatSystemMessage(body: string, language: string = 'ru'): string {
  if (!body) return '';
  if (language === 'en') {
    return SYSTEM_MESSAGES_RU_TO_EN[body] ?? body;
  }
  return SYSTEM_MESSAGES_EN_TO_RU[body] ?? body;
}

export const NOTIFICATIONS_EN_TO_RU: Record<string, string> = {
  // Exact titles requested
  'New offer for your request.': 'Новый отклик по заявке.',
  'New offer for your request': 'Новый отклик по заявке',
  'New chat message.': 'Новое сообщение в чате.',
  'New chat message': 'Новое сообщение в чате',
  'Deal closed / leave a review.': 'Сделка закрыта / можно оставить отзыв.',
  'Deal closed / leave a review': 'Сделка закрыта / можно оставить отзыв.',
  'Verification approved': 'Верификация пройдена',
  'Verification rejected': 'Верификация не пройдена',
  'New request on your route': 'Новая заявка по добавленному маршруту',
  'New request on your route.': 'Новая заявка по добавленному маршруту',
  'Customer selected carrier.': 'Заказчик выбрал перевозчика.',
  'Customer selected carrier': 'Заказчик выбрал перевозчика',
  'Customer selected you': 'Заказчик выбрал перевозчика',
  'Customer selected your offer': 'Заказчик выбрал перевозчика',
  'LOVO invoice issued.': 'Выставлен счёт LOVO.',
  'LOVO invoice issued': 'Выставлен счёт LOVO.',
  'Offers blocked due to unpaid invoice.': 'Отклики заблокированы из-за неоплаченного счёта.',
  'Offers blocked due to unpaid invoice': 'Отклики заблокированы из-за неоплаченного счёта.',
  'Customer confirmed receiving the pet.': 'Клиент подтвердил получение питомца.',
  'Customer confirmed receiving the pet': 'Клиент подтвердил получение питомца.',
  'Deal closed / "Leave a review".': 'Сделка закрыта / “Оставьте отзыв”.',
  'Deal closed / "Leave a review"': 'Сделка закрыта / “Оставьте отзыв”.',
  'Deal closed': 'Сделка закрыта',
  'Transport delivered': 'Перевозка доставлена',
  'Transport delivered.': 'Перевозка доставлена.',
  'Transport started': 'Перевозка начата',
  'Transport started.': 'Перевозка начата.',
  'The carrier marked the transport as delivered. You can leave a review.': 'Перевозчик отметил доставку. Вы можете оставить отзыв.',
  'The carrier marked the transport as delivered.': 'Перевозчик отметил доставку питомца.',
  'The carrier marked the transport as started.': 'Перевозчик начал перевозку.',
  'Fee refund credited to internal balance.': 'Возврат комиссии зачислен на внутренний баланс.',
  'Fee refund credited to internal balance': 'Возврат комиссии зачислен на внутренний баланс.',

  // Descriptions
  'A carrier submitted price and schedule.': 'Перевозчик предложил цену и сроки перевозки.',
  'A carrier sent an offer for your request.': 'Перевозчик предложил цену и сроки перевозки.',
  'The carrier sent you a message.': 'Перевозчик написал вам в чате.',
  'New message in chat.': 'Новое сообщение в чате.',
  'Please share your review about the carrier.': 'Оставьте отзыв о поездке и работе перевозчика.',
  'Transport completed. Please leave a review for the carrier.': 'Оставьте отзыв о поездке и работе перевозчика.',
  'Documents approved. You can now submit offers.': 'Документы одобрены. Доступны отклики на все заявки.',
  'Your documents have been verified. You can now send offers.': 'Документы одобрены. Доступны отклики на все заявки.',
  'Please review remarks and upload valid photos.': 'Проверьте замечания и загрузите корректные фото.',
  'Documents were rejected. Please upload valid documents.': 'Проверьте замечания и загрузите корректные фото.',
  'Please correct the specified issues with your documents.': 'Проверьте замечания модератора и обновите файлы.',
  'New transport on your saved route.': 'Новая перевозка по добавленному направлению.',
  'Offer accepted. Open chat to coordinate details.': 'Отклик принят. Перейдите в чат для согласования.',
  'The customer accepted your offer. Please coordinate details in chat.': 'Отклик принят. Перейдите в чат для согласования.',
  '5% service fee. Pay to unlock sending new offers.': 'Сервисный сбор 5%. Оплатите для отправки новых откликов.',
  'Pay the outstanding invoice to continue working.': 'Погасите неоплаченный счёт для продолжения работы.',
  'The invoice is available under Money and invoices. Open it to review and pay.': 'Счёт доступен в разделе «Деньги и счета». Откройте его для просмотра и оплаты.',
  'Customer confirmed pet delivery.': 'Клиент подтвердил получение питомца.',
  'Transport completed. Please leave a review for the customer.': 'Перевозка завершена. Оставьте отзыв о клиенте.',
  'Funds credited to your internal balance.': 'Средства зачислены на ваш внутренний баланс.',
};

export const NOTIFICATIONS_RU_TO_EN: Record<string, string> = {
  // Exact titles requested
  'Новый отклик по заявке.': 'New offer for your request.',
  'Новый отклик по заявке': 'New offer for your request.',
  'Новое сообщение в чате.': 'New chat message.',
  'Новое сообщение в чате': 'New chat message.',
  'Новое сообщение': 'New chat message.',
  'Сделка закрыта / можно оставить отзыв.': 'Deal closed / leave a review.',
  'Сделка закрыта / можно оставить отзыв': 'Deal closed / leave a review.',
  'Верификация пройдена': 'Verification approved',
  'Верификация подтверждена': 'Verification approved',
  'Верификация не пройдена': 'Verification rejected',
  'Верификация отклонена': 'Verification rejected',
  'Требуются исправления': 'Verification needs changes',
  'Новая заявка по добавленному маршруту': 'New request on your route',
  'Новая заявка по добавленному маршруту.': 'New request on your route.',
  'Заявка по вашему маршруту': 'New request on your route',
  'Заказчик выбрал перевозчика.': 'Customer selected carrier.',
  'Заказчик выбрал перевозчика': 'Customer selected carrier.',
  'Заказчик выбрал вас': 'Customer selected carrier.',
  'Заказчик выбрал вас перевозчиком': 'Customer selected carrier.',
  'Выбран ваш отклик': 'Customer selected carrier.',
  'Выставлен счёт LOVO.': 'LOVO invoice issued.',
  'Выставлен счёт LOVO': 'LOVO invoice issued.',
  'Отклики заблокированы из-за неоплаченного счёта.': 'Offers blocked due to unpaid invoice.',
  'Отклики заблокированы из-за неоплаченного счёта': 'Offers blocked due to unpaid invoice.',
  'Отклики заблокированы': 'Offers blocked due to unpaid invoice.',
  'Клиент подтвердил получение питомца.': 'Customer confirmed receiving the pet.',
  'Клиент подтвердил получение питомца': 'Customer confirmed receiving the pet.',
  'Питомец доставлен': 'Customer confirmed receiving the pet.',
  'Питомец получен': 'Customer confirmed receiving the pet.',
  'Перевозка доставлена': 'Transport delivered',
  'Перевозка начата': 'Transport started',
  'Сделка закрыта / “Оставьте отзыв”.': 'Deal closed / "Leave a review".',
  'Сделка закрыта / "Оставьте отзыв".': 'Deal closed / "Leave a review".',
  'Сделка закрыта': 'Deal closed',
  'Возврат комиссии зачислен на внутренний баланс.': 'Fee refund credited to internal balance.',
  'Возврат комиссии зачислен на внутренний баланс': 'Fee refund credited to internal balance.',
  'Возврат комиссии': 'Fee refund credited to internal balance.',

  // Descriptions
  'Перевозчик предложил цену и сроки перевозки.': 'A carrier submitted price and schedule.',
  'Перевозчик предложил условия по вашей заявке.': 'A carrier submitted price and schedule.',
  'Перевозчик написал вам в чате.': 'The carrier sent you a message.',
  'Оставьте отзыв о поездке и работе перевозчика.': 'Please share your review about the carrier.',
  'Перевозка завершена. Оставьте отзыв о перевозчике.': 'Please share your review about the carrier.',
  'Документы одобрены. Доступны отклики на все заявки.': 'Documents approved. You can now submit offers.',
  'Ваши документы проверены. Теперь вы можете оставлять отклики.': 'Documents approved. You can now submit offers.',
  'Проверьте замечания и загрузите корректные фото.': 'Please review remarks and upload valid photos.',
  'Документы отклонены. Пожалуйста, загрузите корректные документы.': 'Please review remarks and upload valid photos.',
  'Проверьте замечания модератора и обновите файлы.': 'Please review remarks and upload valid files.',
  'Пожалуйста, исправьте указанные замечания по документам.': 'Please review remarks and upload valid files.',
  'Новая перевозка по добавленному направлению.': 'New transport on your saved route.',
  'Новая заявка по вашему маршруту.': 'New transport on your saved route.',
  'Отклик принят. Перейдите в чат для согласования.': 'Offer accepted. Open chat to coordinate details.',
  'Заказчик выбрал ваш отклик. Согласуйте детали в чате.': 'Offer accepted. Open chat to coordinate details.',
  'Сервисный сбор 5%. Оплатите для отправки новых откликов.': '5% service fee. Pay to unlock sending new offers.',
  'LOVO выставил счет на сервисный сбор 5%. До оплаты новые отклики заблокированы.': '5% service fee. Pay to unlock sending new offers.',
  'Выставлен счёт на сервисный сбор LOVO 5%.': '5% service fee. Pay to unlock sending new offers.',
  'Счёт доступен в разделе «Деньги и счета». Откройте его для просмотра и оплаты.': 'The invoice is available under Money and invoices. Open it to review and pay.',
  'Погасите неоплаченный счёт для продолжения работы.': 'Pay the outstanding invoice to continue working.',
  'Оплатите выставленный счёт LOVO для разблокировки откликов.': 'Pay the outstanding invoice to continue working.',
  'Заказчик подтвердил получение питомца.': 'Customer confirmed receiving the pet.',
  'Перевозчик отметил доставку. Вы можете оставить отзыв.': 'The carrier marked the transport as delivered. You can leave a review.',
  'Перевозчик отметил доставку питомца.': 'The carrier marked the transport as delivered.',
  'Перевозчик начал перевозку.': 'The carrier marked the transport as started.',
  'Перевозка завершена. Оставьте отзыв о клиенте.': 'Transport completed. Please leave a review for the customer.',
  'Перевозка завершена. Оставьте отзыв о заказчике.': 'Transport completed. Please leave a review for the customer.',
  'Средства зачислены на ваш внутренний баланс.': 'Funds credited to your internal balance.',
  'Возврат комиссии успешно зачислен на ваш внутренний баланс.': 'Funds credited to your internal balance.',
};

function translateNotificationField(value: string, language: string): string {
  if (!value) return '';
  if (language === 'en') {
    if (NOTIFICATIONS_RU_TO_EN[value]) return NOTIFICATIONS_RU_TO_EN[value];
    const matchMsgs = value.match(/^Новые сообщения от (.+?)(?: \((\d+)\))?$/u);
    if (matchMsgs) return `New messages from ${matchMsgs[1]}${matchMsgs[2] ? ` (${matchMsgs[2]})` : ''}`;
    const matchNeutralMsgs = value.match(/^Сообщения от (.+)$/u);
    if (matchNeutralMsgs) return `Messages from ${matchNeutralMsgs[1]}`;
    const matchNeutralMsg = value.match(/^Сообщение от (.+)$/u);
    if (matchNeutralMsg) return `Message from ${matchNeutralMsg[1]}`;
    const matchMsg = value.match(/^Новое сообщение от (.+)$/u);
    if (matchMsg) return `New message from ${matchMsg[1]}`;
    const matchOffer = value.match(/^Новый отклик от (.+)$/u);
    if (matchOffer) return `New offer from ${matchOffer[1]}`;
    const matchInvoice = value.match(/^Выставлен счёт (LOVO-\d+)$/u);
    if (matchInvoice) return `Invoice ${matchInvoice[1]} issued`;
    return value;
  }
  if (NOTIFICATIONS_EN_TO_RU[value]) return NOTIFICATIONS_EN_TO_RU[value];
  const matchMsgs = value.match(/^New messages from (.+?)(?: \((\d+)\))?$/iu);
  if (matchMsgs) return `Новые сообщения от ${matchMsgs[1]}${matchMsgs[2] ? ` (${matchMsgs[2]})` : ''}`;
  const matchNeutralMsgs = value.match(/^Messages from (.+)$/iu);
  if (matchNeutralMsgs) return `Сообщения от ${matchNeutralMsgs[1]}`;
  const matchNeutralMsg = value.match(/^Message from (.+)$/iu);
  if (matchNeutralMsg) return `Сообщение от ${matchNeutralMsg[1]}`;
  const matchMsg = value.match(/^New message from (.+)$/iu);
  if (matchMsg) return `Новое сообщение от ${matchMsg[1]}`;
  const matchOffer = value.match(/^New offer from (.+)$/iu);
  if (matchOffer) return `Новый отклик от ${matchOffer[1]}`;
  const matchInvoice = value.match(/^Invoice (LOVO-\d+) issued$/iu);
  if (matchInvoice) return `Выставлен счёт ${matchInvoice[1]}`;
  return value;
}

export function formatNotification(title: string, text: string, language: string = 'ru'): { title: string; text: string } {
  return {
    title: translateNotificationField(title, language),
    text: translateNotificationField(text, language),
  };
}
